import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import './RainbowChatbot.css';

const RainbowChatbot = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi there! 🌈 I\'m Rainbow, your AI assistant. I can help you with questions about our products, shipping, returns, and more. How can I help you today?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationStep, setVerificationStep] = useState('phone'); // 'phone' | 'code' | 'verified'
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Detect user location on mount
  useEffect(() => {
    const detectLocation = async () => {
      try {
        // Get timezone from browser
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const locale = navigator.language || navigator.userLanguage;

        // Try to detect country from timezone or locale
        let country = 'AU'; // Default to Australia
        if (locale) {
          const localeMatch = locale.match(/-([A-Z]{2})$/);
          if (localeMatch) {
            country = localeMatch[1];
          }
        }

        // Try to get country from IP geolocation (free service)
        try {
          const geoResponse = await fetch('https://ipapi.co/json/');
          if (geoResponse.ok) {
            const geoData = await geoResponse.json();
            if (geoData.country_code) {
              country = geoData.country_code;
            }
          }
        } catch (geoError) {
          console.log('🌍 [CHATBOT] Geo-location detection failed, using defaults');
        }

        setUserLocation({
          country,
          timezone,
          locale
        });
      } catch (error) {
        console.error('❌ [CHATBOT] Error detecting location:', error);
        // Default to Australia
        setUserLocation({
          country: 'AU',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: navigator.language || 'en-AU'
        });
      }
    };

    detectLocation();
  }, []);

  // Use Mango API configuration - use unified config
  const getApiBase = () => {
    console.log('🔗 [CHATBOT] Using API base URL:', API_BASE_URL);
    return API_BASE_URL.replace('/api/v1', ''); // Remove /api/v1 since route includes it
  };
  
  // Debug: Log the API base URL being used
  useEffect(() => {
    const apiBase = getApiBase();
    console.log('🔗 [CHATBOT] Final API Base URL:', apiBase);
    console.log('🔗 [CHATBOT] REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
    console.log('🔗 [CHATBOT] REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    console.log('🔗 [CHATBOT] Window protocol:', window.location.protocol);
    console.log('🔗 [CHATBOT] Window hostname:', window.location.hostname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const sendMessage = async (messageText) => {
    const requestId = `frontend-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const startTime = Date.now();
    
    console.log(`\n${'='.repeat(80)}`);
    console.log(`🔵 [CHATBOT-FRONTEND] [${requestId}] NEW MESSAGE SENT`);
    console.log(`🔵 [CHATBOT-FRONTEND] [${requestId}] Timestamp: ${new Date().toISOString()}`);
    console.log(`${'='.repeat(80)}\n`);

    if (!messageText.trim()) {
      console.log(`❌ [CHATBOT-FRONTEND] [${requestId}] [VALIDATION] Message is empty, aborting`);
      return;
    }

    console.log(`📥 [CHATBOT-FRONTEND] [${requestId}] [DATA] Message text: ${messageText}`);
    console.log(`📥 [CHATBOT-FRONTEND] [${requestId}] [DATA] Message length: ${messageText.length} characters`);

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    // FUNCTION: Update UI State
    const uiUpdateStart = Date.now();
    console.log(`\n🎨 [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Update UI State Started`);
    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      console.log(`🎨 [CHATBOT-FRONTEND] [${requestId}] [DATA] Total messages after adding user message: ${newMessages.length}`);
      return newMessages;
    });
    setInput('');
    setIsLoading(true);
    const uiUpdateTime = Date.now() - uiUpdateStart;
    console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Update UI State Completed (${uiUpdateTime}ms)`);

    try {
      // FUNCTION: Build Conversation History
      const historyStart = Date.now();
      console.log(`\n📚 [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Build Conversation History Started`);
      console.log(`📚 [CHATBOT-FRONTEND] [${requestId}] [DATA] Current messages count: ${messages.length}`);
      
      const conversationHistory = messages
        .filter(msg => msg.role !== 'system')
        .map(msg => ({
          role: msg.role === 'assistant' ? 'assistant' : 'user',
          content: msg.content
        }));
      
      const historyTime = Date.now() - historyStart;
      console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Build Conversation History Completed (${historyTime}ms)`);
      console.log(`📚 [CHATBOT-FRONTEND] [${requestId}] [DATA] Conversation history length: ${conversationHistory.length}`);
      console.log(`📚 [CHATBOT-FRONTEND] [${requestId}] [DATA] Conversation history preview:`, conversationHistory.map(m => ({
        role: m.role,
        contentLength: m.content?.length || 0
      })));

      // FUNCTION: Prepare Request Payload
      const payloadStart = Date.now();
      console.log(`\n📦 [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Prepare Request Payload Started`);
      // Get customer ID if logged in
      const customerId = localStorage.getItem('customer_data') 
        ? JSON.parse(localStorage.getItem('customer_data')).id 
        : null;
      
      // Generate session ID for anonymous users
      let sessionId = sessionStorage.getItem('chatbot_session_id');
      if (!sessionId) {
        sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('chatbot_session_id', sessionId);
      }
      
      const requestPayload = {
        message: messageText,
        conversationHistory,
        userLocation,
        customerId,
        sessionId,
      };
      const payloadTime = Date.now() - payloadStart;
      console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Prepare Request Payload Completed (${payloadTime}ms)`);
      console.log(`📦 [CHATBOT-FRONTEND] [${requestId}] [DATA] Request payload size: ${JSON.stringify(requestPayload).length} bytes`);
      console.log(`📦 [CHATBOT-FRONTEND] [${requestId}] [DATA] User location:`, userLocation);
      const apiBase = getApiBase();
      console.log(`📦 [CHATBOT-FRONTEND] [${requestId}] [DATA] API Base URL: ${apiBase}`);

      // FUNCTION: Send API Request
      const apiStart = Date.now();
      console.log(`\n🚀 [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Send API Request Started`);
      console.log(`🚀 [CHATBOT-FRONTEND] [${requestId}] [DATA] API Endpoint: ${apiBase}/api/chatbot/chat`);
      console.log(`🚀 [CHATBOT-FRONTEND] [${requestId}] [DATA] Request method: POST`);
      
      const response = await fetch(`${apiBase}/api/v1/chatbot/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      const apiTime = Date.now() - apiStart;
      console.log(`⏱️ [CHATBOT-FRONTEND] [${requestId}] [TIMING] API request took: ${apiTime}ms`);
      console.log(`🚀 [CHATBOT-FRONTEND] [${requestId}] [DATA] Response status: ${response.status} ${response.statusText}`);
      console.log(`🚀 [CHATBOT-FRONTEND] [${requestId}] [DATA] Response ok: ${response.ok}`);

      // FUNCTION: Parse Response
      const parseStart = Date.now();
      console.log(`\n📥 [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Parse Response Started`);
      const data = await response.json();
      const parseTime = Date.now() - parseStart;
      console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Parse Response Completed (${parseTime}ms)`);
      console.log(`📥 [CHATBOT-FRONTEND] [${requestId}] [DATA] Response success: ${data.success}`);
      if (data._debug) {
        console.log(`📥 [CHATBOT-FRONTEND] [${requestId}] [DEBUG] Backend request ID: ${data._debug.requestId}`);
        console.log(`📥 [CHATBOT-FRONTEND] [${requestId}] [DEBUG] Backend total time: ${data._debug.totalTime}ms`);
        console.log(`📥 [CHATBOT-FRONTEND] [${requestId}] [DEBUG] Backend timings:`, data._debug.timings);
      }

      if (data.success) {
        // FUNCTION: Process Successful Response
        const processStart = Date.now();
        console.log(`\n✅ [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Process Successful Response Started`);
        console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] [DATA] AI message length: ${data.message?.length || 0} characters`);
        console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] [DATA] Has navigation: ${!!data.navigation}`);
        console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] [DATA] Has scroll: ${!!data.scroll}`);
        
        const assistantMessage = {
          role: 'assistant',
          content: data.message,
          timestamp: data.timestamp || new Date().toISOString()
        };
        
        setMessages(prev => {
          const newMessages = [...prev, assistantMessage];
          console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] [DATA] Total messages after adding assistant message: ${newMessages.length}`);
          return newMessages;
        });

        // Handle navigation commands
        if (data.navigation && data.navigation.url) {
          console.log(`🧭 [CHATBOT-FRONTEND] [${requestId}] [NAVIGATION] Navigation command detected: ${data.navigation.url}`);
          const scrollTarget = data.scroll && data.scroll.target ? data.scroll.target : null;
          if (scrollTarget) {
            console.log(`🧭 [CHATBOT-FRONTEND] [${requestId}] [NAVIGATION] Scroll target: ${scrollTarget}`);
          }
          handleNavigation(data.navigation.url, scrollTarget);
        }

        // Check if response mentions order verification
        if (data.message.toLowerCase().includes('order') && 
            (data.message.toLowerCase().includes('email') || data.message.toLowerCase().includes('phone'))) {
          console.log(`📱 [CHATBOT-FRONTEND] [${requestId}] [VERIFICATION] Order verification prompt detected`);
          setShowPhoneVerification(true);
        }
        
        const processTime = Date.now() - processStart;
        console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Process Successful Response Completed (${processTime}ms)`);
      } else {
        // FUNCTION: Handle Error Response
        console.log(`\n❌ [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Handle Error Response Started`);
        console.log(`❌ [CHATBOT-FRONTEND] [${requestId}] [ERROR] Response error:`, data.error);
        if (data.loopDetected) {
          console.log(`⚠️ [CHATBOT-FRONTEND] [${requestId}] [LOOP DETECTION] Loop detected by backend!`);
        }
        
        const errorMessage = {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again! 🌈',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
        console.log(`❌ [CHATBOT-FRONTEND] [${requestId}] [FUNCTION] Handle Error Response Completed`);
      }
    } catch (error) {
      const errorTime = Date.now() - startTime;
      console.error(`\n${'='.repeat(80)}`);
      console.error(`❌ [CHATBOT-FRONTEND] [${requestId}] REQUEST FAILED`);
      console.error(`❌ [CHATBOT-FRONTEND] [${requestId}] [ERROR] Error sending message:`, error);
      console.error(`❌ [CHATBOT-FRONTEND] [${requestId}] [ERROR] Error stack:`, error.stack);
      console.error(`⏱️ [CHATBOT-FRONTEND] [${requestId}] [TIMING] Total time before error: ${errorTime}ms`);
      console.error(`${'='.repeat(80)}\n`);
      
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I\'m having trouble connecting. Please try again later! 🌈',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      const totalTime = Date.now() - startTime;
      setIsLoading(false);
      console.log(`\n${'='.repeat(80)}`);
      console.log(`✅ [CHATBOT-FRONTEND] [${requestId}] REQUEST COMPLETED`);
      console.log(`⏱️ [CHATBOT-FRONTEND] [${requestId}] [TIMING] Total request time: ${totalTime}ms`);
      console.log(`${'='.repeat(80)}\n`);
    }
  };

  const handleSend = () => {
    if (input.trim() && !isLoading) {
      sendMessage(input);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSendPhoneCode = async () => {
    if (!phoneNumber.trim()) {
      alert('Please enter your phone number');
      return;
    }

    try {
      const response = await fetch(`${getApiBase()}/api/chatbot/verify/phone/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone: phoneNumber })
      });

      const data = await response.json();

      if (data.success) {
        setVerificationStep('code');
        const message = {
          role: 'assistant',
          content: `📱 Verification code sent to ${phoneNumber}! Please enter the code below.`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, message]);
      } else {
        alert(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('❌ [CHATBOT] Error sending verification code:', error);
      alert('Failed to send verification code. Please try again.');
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      alert('Please enter the verification code');
      return;
    }

    try {
      const response = await fetch(`${getApiBase()}/api/chatbot/verify/phone/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone: phoneNumber,
          code: verificationCode
        })
      });

      const data = await response.json();

      if (data.success && data.verified) {
        setPhoneVerified(true);
        setVerificationStep('verified');
        const message = {
          role: 'assistant',
          content: '✅ Phone number verified! I can now look up your orders. What would you like to know?',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, message]);
        setShowPhoneVerification(false);
      } else {
        alert(data.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('❌ [CHATBOT] Error verifying code:', error);
      alert('Failed to verify code. Please try again.');
    }
  };

  // Handle navigation commands from Rainbow
  const handleNavigation = (url, scrollTarget = null) => {
    console.log('🧭 [CHATBOT] Navigating to:', url, scrollTarget ? `Scroll to: ${scrollTarget}` : '');
    
    // Extract hash from URL if present (e.g., /policies#returns -> returns)
    const urlHash = url.includes('#') ? url.split('#')[1] : null;
    const finalScrollTarget = scrollTarget || urlHash;
    
    // Get current pathname (without hash)
    const currentPath = window.location.pathname;
    const targetPath = url.split('#')[0]; // Remove hash from URL for path comparison
    
    // Check if we're already on the same page
    const isSamePage = currentPath === targetPath;
    
    console.log('🧭 [CHATBOT] Current path:', currentPath);
    console.log('🧭 [CHATBOT] Target path:', targetPath);
    console.log('🧭 [CHATBOT] Is same page:', isSamePage);
    console.log('🧭 [CHATBOT] Final scroll target:', finalScrollTarget);
    
    // If we're on the same page, just scroll (no navigation needed)
    if (isSamePage && finalScrollTarget) {
      console.log('🧭 [CHATBOT] Already on same page, scrolling to section...');
      setTimeout(() => {
        scrollToSection(finalScrollTarget);
      }, 100); // Small delay to ensure DOM is ready
    } else {
      // Navigate to the URL
      navigate(url);
      
      // Wait a bit for page to load, then scroll
      setTimeout(() => {
        if (finalScrollTarget) {
          scrollToSection(finalScrollTarget);
        }
      }, 500); // Wait 500ms for navigation to complete
    }
  };
  
  // Helper function to scroll to a section
  const scrollToSection = (sectionId) => {
    console.log('🧭 [CHATBOT] Attempting to scroll to section:', sectionId);
    
    // Try multiple selectors to find the element
    const selectors = [
      `#${sectionId}`, // Direct ID
      `[id="${sectionId}"]`, // ID attribute
      `[data-section="${sectionId}"]`, // Data attribute
      `[name="${sectionId}"]`, // Name attribute
      `.${sectionId}` // Class name (fallback)
    ];
    
    let element = null;
    for (const selector of selectors) {
      element = document.querySelector(selector);
      if (element) {
        console.log('🧭 [CHATBOT] Found element with selector:', selector);
        break;
      }
    }
    
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      console.log('✅ [CHATBOT] Scrolled to:', sectionId);
    } else {
      console.log('⚠️ [CHATBOT] Could not find scroll target:', sectionId);
      console.log('⚠️ [CHATBOT] Tried selectors:', selectors);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleLookupOrders = async (email) => {
    try {
      const response = await fetch(`${getApiBase()}/api/chatbot/orders/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email || undefined,
          phone: phoneVerified ? phoneNumber : undefined
        })
      });

      const data = await response.json();

      if (data.success && data.orders) {
        if (data.orders.length === 0) {
          const message = {
            role: 'assistant',
            content: 'I couldn\'t find any orders for that email or phone number. Double-check your details, or sign in and open My Account → Support for help. You can also email support@rainbow-sails.shop 🌈',
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, message]);
        } else {
          const ordersText = data.orders.map(order => 
            `Order #${order.orderNumber}: ${order.status} - $${order.total} ${order.currency || 'AUD'}`
          ).join('\n');

          const message = {
            role: 'assistant',
            content: `Here are your orders:\n\n${ordersText}\n\nWould you like details about a specific order? 🌈`,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, message]);
        }
      } else if (data.requiresVerification) {
        setShowPhoneVerification(true);
        const message = {
          role: 'assistant',
          content: 'Please verify your phone number to look up orders.',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, message]);
      } else {
        const message = {
          role: 'assistant',
          content: data.error || 'Unable to lookup orders. Try My Account → Support after signing in, or email support@rainbow-sails.shop 🌈',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, message]);
      }
    } catch (error) {
      console.error('❌ [CHATBOT] Error looking up orders:', error);
      const message = {
        role: 'assistant',
        content: 'Sorry, I\'m having trouble looking up orders. Try again, or use My Account → Support (sign in). Email: support@rainbow-sails.shop 🌈',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, message]);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        className="rainbow-chatbot-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open Rainbow chatbot"
      >
        🤖
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="rainbow-chatbot-window">
          <div className="rainbow-chatbot-header">
            <div className="rainbow-chatbot-title">
              <span className="rainbow-emoji">🌈</span>
              <span>Rainbow</span>
            </div>
            <button
              className="rainbow-chatbot-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          {/* Phone Verification Panel */}
          {showPhoneVerification && !phoneVerified && (
            <div className="rainbow-chatbot-verification">
              {verificationStep === 'phone' && (
                <div className="verification-step">
                  <h3>📱 Verify Phone Number</h3>
                  <p>Enter your phone number to receive a verification code:</p>
                  <input
                    type="tel"
                    placeholder="+61 4XX XXX XXX"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="verification-input"
                  />
                  <button onClick={handleSendPhoneCode} className="verification-button">
                    Send Code
                  </button>
                  <button
                    onClick={() => setShowPhoneVerification(false)}
                    className="verification-cancel"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {verificationStep === 'code' && (
                <div className="verification-step">
                  <h3>🔐 Enter Verification Code</h3>
                  <p>Enter the 6-digit code sent to {phoneNumber}:</p>
                  <input
                    type="text"
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="verification-input"
                    maxLength={6}
                  />
                  <button onClick={handleVerifyCode} className="verification-button">
                    Verify
                  </button>
                  <button
                    onClick={() => {
                      setVerificationStep('phone');
                      setVerificationCode('');
                    }}
                    className="verification-cancel"
                  >
                    Back
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          <div className="rainbow-chatbot-messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`rainbow-chatbot-message ${msg.role === 'user' ? 'user' : 'assistant'}`}
              >
                <div className="message-content">
                  {msg.content.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < msg.content.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="rainbow-chatbot-message assistant">
                <div className="message-content">
                  <span className="typing-indicator">...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="rainbow-chatbot-input-container">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="rainbow-chatbot-input"
              disabled={isLoading}
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="rainbow-chatbot-send"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default RainbowChatbot;
